/**
 * Store Pinia du module « Matériel collaborateur » (V2) — branché sur l'API
 * boostcore (`/material/*` via `src/api/materialApi.ts`).
 *
 * Le store charge TOUTES les cases (dont l'inventaire baseline) + les
 * remplacements, et dérive localement la vue collaborateur (groupée par email)
 * comme avant — seule la SOURCE change (API au lieu du mock). Les mutations
 * appellent l'API puis rechargent. Le détenteur est identifié par `userUuid`
 * (mutations ET navigation : la fiche collaborateur est routée sur `userUuid`).
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { computeProgress, isReplacementActive } from '@/v2/onboarding/catalog'
import * as api from '@/api/materialApi'
import type {
  AssetAssignment,
  AssetStatus,
  CaseType,
  Collaborator,
  CollaboratorView,
  HandoverDocument,
  OnboardingCase,
  OwnedAsset,
  ReplacementSource,
  ReplacementTicket,
} from '@/v2/onboarding/types'

/* -------------------------------------------------------------------------- */
/* Mappers API → types front                                                  */
/* -------------------------------------------------------------------------- */

function mapAsset(a: api.ApiAsset): AssetAssignment {
  return {
    id: a.id,
    typeCode: a.typeCode,
    label: a.label,
    category: a.category,
    status: a.status,
    owner: a.ownerName,
    attributes: a.attributes ?? {},
    orderedAt: a.orderedAt,
    reminderAt: a.reminderAt,
    expectedAt: a.expectedAt,
    deliveredAt: a.deliveredAt,
    returnedAt: a.returnedAt,
    condition: a.condition ?? undefined,
    returnCondition: a.returnCondition ?? undefined,
    notes: a.notes ?? undefined,
    replacesAssignmentId: a.replacesId,
    history: [],
  }
}

function mapCollaborator(idn: api.ApiCollaboratorIdentity | null | undefined, fallbackUuid: string): Collaborator {
  return {
    userUuid: idn?.userUuid ?? fallbackUuid,
    firstName: idn?.firstName ?? '',
    lastName: idn?.lastName ?? '',
    email: idn?.email ?? '',
    entity: idn?.entity ?? '',
    jobTitle: idn?.jobTitle ?? '',
    manager: idn?.manager ?? '',
    agency: idn?.agency ?? undefined,
    mobile: idn?.mobile ?? undefined,
    phone3cx: idn?.phone3cx ?? undefined,
  }
}

function mapCase(c: api.ApiCase): OnboardingCase {
  return {
    id: c.id,
    reference: c.reference,
    type: c.type,
    status: c.status,
    source: c.source,
    collaborator: mapCollaborator(c.collaborator, c.userUuid),
    arrivalDate: c.arrivalDate,
    exitDate: c.exitDate,
    comment: c.comment ?? undefined,
    createdAt: c.createdAt,
    createdBy: c.createdBy ?? '',
    assets: (c.assets ?? []).map(mapAsset),
    handovers: [],
    baseline: c.baseline,
  }
}

export const useOnboardingStore = defineStore('onboarding', () => {
  const cases = ref<OnboardingCase[]>([])
  const replacementTickets = ref<ReplacementTicket[]>([])
  const loading = ref(false)
  const error = ref(false)
  const loaded = ref(false)

  /** Filtre courant de la liste : tous / onboarding / offboarding. */
  const typeFilter = ref<'ALL' | CaseType>('ALL')

  /** Dossiers « réels » (demandes), hors inventaire baseline. */
  const realCases = computed(() => cases.value.filter((c) => !c.baseline))

  const filteredCases = computed(() =>
    typeFilter.value === 'ALL'
      ? realCases.value
      : realCases.value.filter((c) => c.type === typeFilter.value),
  )

  const onboardings = computed(() => realCases.value.filter((c) => c.type === 'ONBOARDING'))
  const offboardings = computed(() => realCases.value.filter((c) => c.type === 'OFFBOARDING'))

  const counts = computed(() => ({
    total: realCases.value.length,
    onboarding: onboardings.value.length,
    offboarding: offboardings.value.length,
    draft: realCases.value.filter((c) => c.status === 'DRAFT').length,
    inProgress: realCases.value.filter((c) => c.status === 'IN_PROGRESS').length,
    done: realCases.value.filter((c) => c.status === 'DONE').length,
    activeReplacements: replacementTickets.value.filter((t) => isReplacementActive(t.status)).length,
  }))

  /** Inventaire agrégé par collaborateur (clé = email), dérivé des cases + tickets. */
  const collaborators = computed<CollaboratorView[]>(() => {
    const map = new Map<string, CollaboratorView>()
    for (const c of cases.value) {
      const col = c.collaborator
      let entry = map.get(col.email)
      if (!entry) {
        entry = {
          email: col.email,
          userUuid: col.userUuid,
          firstName: col.firstName,
          lastName: col.lastName,
          entity: col.entity,
          jobTitle: col.jobTitle,
          manager: col.manager,
          agency: col.agency,
          mobile: col.mobile,
          phone3cx: col.phone3cx,
          caseIds: [],
          ownedAssets: [],
          pendingAssets: [],
          tickets: [],
        }
        map.set(col.email, entry)
      }
      entry.caseIds.push(c.id)
      for (const a of c.assets) {
        const owned: OwnedAsset = { ...a, caseId: c.id }
        if (a.status === 'DELIVERED') entry.ownedAssets.push(owned)
        else if (a.status !== 'REPLACED' && a.status !== 'RETURNED') entry.pendingAssets.push(owned)
      }
    }
    for (const t of replacementTickets.value) {
      map.get(t.collaboratorEmail)?.tickets.push(t)
    }
    return Array.from(map.values())
  })

  function setTypeFilter(value: 'ALL' | CaseType) {
    typeFilter.value = value
  }

  function getCase(id: string): OnboardingCase | null {
    return cases.value.find((c) => c.id === id) ?? null
  }

  function getCollaboratorByUuid(userUuid: string): CollaboratorView | null {
    return collaborators.value.find((c) => c.userUuid === userUuid) ?? null
  }

  function getTicket(ticketId: string): ReplacementTicket | null {
    return replacementTickets.value.find((t) => t.id === ticketId) ?? null
  }

  function progressOf(c: OnboardingCase): number {
    return computeProgress(c)
  }

  /* ── Chargement (API) ─────────────────────────────────────────────────── */

  async function load() {
    loading.value = true
    error.value = false
    try {
      const res = await api.fetchCases({ includeBaseline: true, pageSize: 1000 })
      cases.value = res.data.map(mapCase)

      // Résolveurs pour enrichir les tickets (email + caseId à partir des cases).
      const emailByUuid = new Map<string, string>()
      const caseIdByAsset = new Map<string, string>()
      for (const c of cases.value) {
        if (c.collaborator.userUuid) emailByUuid.set(c.collaborator.userUuid, c.collaborator.email)
        for (const a of c.assets) caseIdByAsset.set(a.id, c.id)
      }

      const rows = await api.fetchReplacements()
      replacementTickets.value = rows.map((r) => ({
        id: r.id,
        reference: r.reference,
        userUuid: r.userUuid,
        collaboratorEmail: emailByUuid.get(r.userUuid) ?? '',
        caseId: caseIdByAsset.get(r.oldAssetId) ?? caseIdByAsset.get(r.newAssetId) ?? '',
        oldAssignmentId: r.oldAssetId,
        newAssignmentId: r.newAssetId,
        status: r.status,
        reason: r.reason,
        source: r.source as ReplacementSource,
        externalOrder: r.externalOrder,
        reminderAt: r.reminderAt,
        notes: r.notes ?? undefined,
        createdAt: r.createdAt,
        createdBy: '',
        history: [],
      }))
      loaded.value = true
    } catch {
      error.value = true
    } finally {
      loading.value = false
    }
  }

  /** Charge une seule fois (au montage des pages). */
  async function ensureLoaded() {
    if (!loaded.value && !loading.value) await load()
  }

  /** Recherche d'utilisateurs (sélecteur collaborateur). */
  function searchUsers(q: string) {
    return api.searchUsers(q)
  }

  /** Inventaire détenu (objets Remis) d'un collaborateur — pour l'offboarding. */
  async function fetchCollaboratorInventory(userUuid: string): Promise<OwnedAsset[]> {
    const detail = await api.fetchCollaborator(userUuid)
    return detail.ownedAssets.map((a) => ({ ...mapAsset(a), caseId: a.caseId ?? '' }))
  }

  /* ── Mutations (API + reload) ─────────────────────────────────────────── */

  /** Crée une demande (onboarding/offboarding) pour un User existant. Retourne l'id. */
  async function createCase(input: {
    type: CaseType
    userUuid: string
    arrivalDate?: string | null
    exitDate?: string | null
    comment?: string
    assetTypeCodes: string[]
  }): Promise<string> {
    const created = await api.createCase({
      type: input.type,
      userUuid: input.userUuid,
      arrivalDate: input.arrivalDate ?? null,
      exitDate: input.exitDate ?? null,
      comment: input.comment,
      assetTypeCodes: input.assetTypeCodes,
    })
    await load()
    return created.id
  }

  /** Affecte du matériel existant à un collaborateur (rattrapage). */
  async function assignAssetsToCollaborator(
    userUuid: string,
    items: Array<{
      typeCode: string
      attributes?: Record<string, string>
      status?: AssetStatus
      ownerName?: string
      condition?: string
    }>,
  ) {
    await api.assignAssets(userUuid, items)
    await load()
  }

  /**
   * Change le statut d'un objet (historique géré côté back). `opts.condition` à la
   * remise (DELIVERED), `opts.returnCondition` à la restitution (RETURNED).
   */
  async function setAssetStatus(
    assetId: string,
    toStatus: AssetStatus,
    opts?: { reason?: string; condition?: string; returnCondition?: string },
  ) {
    await api.updateAssetStatus(assetId, toStatus, opts)
    await load()
  }

  /** Édite un objet (admin) puis recharge. */
  async function updateAsset(
    assetId: string,
    changes: {
      label?: string
      ownerName?: string
      status?: AssetStatus
      attributes?: Record<string, string>
      notes?: string
      reminderAt?: string | null
      condition?: string | null
      returnCondition?: string | null
      orderedAt?: string | null
      expectedAt?: string | null
      deliveredAt?: string | null
      returnedAt?: string | null
    },
  ) {
    await api.updateAsset(assetId, changes)
    await load()
  }

  /** Supprime un objet (admin) puis recharge. */
  async function deleteAsset(assetId: string) {
    await api.deleteAsset(assetId)
    await load()
  }

  /** Demande de remplacement. Retourne l'id du ticket. */
  async function requestReplacement(input: {
    userUuid: string
    oldAssignmentId: string
    reason: string
    source: ReplacementSource
    externalOrder: boolean
    reminderAt?: string | null
    notes?: string
  }): Promise<string> {
    const ticket = await api.requestReplacement({
      userUuid: input.userUuid,
      oldAssetId: input.oldAssignmentId,
      reason: input.reason,
      source: input.source,
      externalOrder: input.externalOrder,
      reminderAt: input.reminderAt ?? null,
      notes: input.notes,
    })
    await load()
    return ticket.id
  }

  /** Supprime une demande (le matériel détenu est conservé côté back). */
  async function deleteCase(id: string) {
    await api.deleteCase(id)
    await load()
  }

  async function advanceReplacement(ticketId: string) {
    await api.advanceReplacement(ticketId)
    await load()
  }

  async function cancelReplacement(ticketId: string) {
    await api.cancelReplacement(ticketId)
    await load()
  }

  /**
   * Fiche de remise : pas encore d'endpoint back (P4) → enregistrement LOCAL
   * non persisté (perdu au reload). À brancher quand l'endpoint handover existera.
   */
  function saveHandover(caseId: string, doc: HandoverDocument) {
    const c = getCase(caseId)
    if (!c) return
    if (!c.handovers) c.handovers = []
    c.handovers.push(doc)
  }

  return {
    cases,
    replacementTickets,
    loading,
    error,
    loaded,
    typeFilter,
    filteredCases,
    onboardings,
    offboardings,
    counts,
    collaborators,
    ensureLoaded,
    load,
    reload: load,
    searchUsers,
    fetchCollaboratorInventory,
    setTypeFilter,
    getCase,
    getCollaboratorByUuid,
    getTicket,
    progressOf,
    createCase,
    deleteCase,
    assignAssetsToCollaborator,
    setAssetStatus,
    updateAsset,
    deleteAsset,
    requestReplacement,
    advanceReplacement,
    cancelReplacement,
    saveHandover,
  }
})
